import { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table
} from "reactstrap";
import swal from "sweetalert";
import { LoadingSpinner, isUrdu } from "../../../constants/const";
import UpdateFinanceAccounts from "../../config/finance/financeAccountCategories/UpdateFinanceAccounts";
import UpdateFinanceDimensions from "../../config/finance/financeAccountCategories/UpdateFinanceDimensions";
import { ApiFinance } from "../../funds/ApiFinance";
import { GetLanguageString, NoResult } from "../../helper/Components";
import { GetSelectedLanguage } from "../../helper/Method";

const ManageUnitAccounts = ({ setModal, unitId, unitName, unitTypeNameEn }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const [activeTab, setActiveTab] = useState("1");
  const [dimensionsList, setDimensionsList] = useState([]);
  const [accountsList, setAccountsList] = useState([]);
  const [accountSubCategories, setAccountSubCategories] = useState([]);
  const [dimensionSubCategories, setDimensionSubCategories] = useState([]);

  // Tab 1 state
  const [selectedUnitDimension, setSelectedUnitDimension] = useState(null);
  const [initialUnitDimension, setInitialUnitDimension] = useState(null);
  const [selectedUnitCashInHandAccount, setSelectedUnitCashInHandAccount] = useState(null);
  const [selectedUnitBankAccounts, setSelectedUnitBankAccounts] = useState([]);

  // Tab 2 state
  const [updateAccountModal, setUpdateAccountModal] = useState(false);
  const [updateDimensionModal, setUpdateDimensionModal] = useState(false);
  const [financeAccountCategoryId, setFinanceAccountCategoryId] = useState(null);
  const [selectedFinanceAccounts, setSelectedFinanceAccounts] = useState([]);
  const [selectedFinanceDimensions, setSelectedFinanceDimensions] = useState([]);
  const [reload, setReload] = useState(false);
  const [errors, setErrors] = useState({});

  const toggle = () => setModal(false);

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  useEffect(() => {
    setLoading(true);
    setButtonDisable(true);

    Promise.all([
      ApiFinance().GetFinanceAccountDetail(unitId),
      ApiFinance().GetFinanceSubCategoriesForUnit(unitId, "accounts"),
      ApiFinance().GetFinanceSubCategoriesForUnit(unitId, "dimensions"),
    ])
      .then(([detailRes, accountCatRes, dimCatRes]) => {
        setAccountSubCategories(accountCatRes.data || []);
        setDimensionSubCategories(dimCatRes.data || []);

        setDimensionsList(detailRes.data.dimensionsList || []);
        setAccountsList(detailRes.data.accountsList || []);

        setSelectedUnitDimension(detailRes.data.unitDimension || null);
        setInitialUnitDimension(detailRes.data.unitDimension || null);

        setSelectedUnitCashInHandAccount(detailRes.data.cashInHandAccount || null);

        setSelectedUnitBankAccounts(
          detailRes.data.bankAccounts || (detailRes.data.amanatAccount ? [detailRes.data.amanatAccount] : [])
        );

        setLoading(false);
        setButtonDisable(false);
      })
      .catch((err) => {
        console.log(err);
        swal({
          title: err.message || "Error fetching data",
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
        setButtonDisable(false);
      });
  }, [unitId, reload, unitTypeNameEn]);

  const handleOnUpdateAccounts = (record) => {
    setFinanceAccountCategoryId(record.id);
    setSelectedFinanceAccounts(record.accounts || []);
    setUpdateAccountModal(true);
  };

  const handleOnUpdateDimensions = (record) => {
    setFinanceAccountCategoryId(record.id);
    setSelectedFinanceDimensions(record.dimensions || []);
    setUpdateDimensionModal(true);
  };

  const validate = () => {
    let temp = {};
    if (!selectedUnitDimension && !initialUnitDimension) {
      temp.dimensionError = (
        <GetLanguageString props="finance_unit_manage_account_select_dimension_error" />
      );
    }
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      unitId,
      unitDimension: selectedUnitDimension || null,
      unitCashInHandAccount: selectedUnitCashInHandAccount || null,
      unitBankAccounts: selectedUnitBankAccounts || [],
      unitAmanatAccount: selectedUnitBankAccounts && selectedUnitBankAccounts.length > 0 ? selectedUnitBankAccounts[0] : null,
    };

    setLoading(true);
    setButtonDisable(true);
    ApiFinance()
      .UpdateUnitFinanceAccount(payload)
      .then((res) => {
        swal({
          title: t("finance_unit_account_update"),
          className: GetSelectedLanguage() === "en" ? "" : "ur",
          icon: "success",
          buttons: "OK",
        });
        toggle();
        setLoading(false);
        setButtonDisable(false);
      })
      .catch((err) => {
        console.log(err);
        swal({
          title: err,
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
        setButtonDisable(false);
      });
  };

  return (
    <div>
      {updateAccountModal && (
        <UpdateFinanceAccounts
          setUpdateModal={setUpdateAccountModal}
          financeAccountCategoryId={financeAccountCategoryId}
          previouslySelectedFinanceAccounts={selectedFinanceAccounts}
          reload={reload}
          setReload={setReload}
          unitId={unitId}
        />
      )}
      {updateDimensionModal && (
        <UpdateFinanceDimensions
          setUpdateModal={setUpdateDimensionModal}
          financeAccountCategoryId={financeAccountCategoryId}
          previouslySelectedFinanceDimensions={selectedFinanceDimensions}
          reload={reload}
          setReload={setReload}
          unitId={unitId}
        />
      )}

      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal isOpen={true} className="inmodal" size="xl" autoFocus={false}>
          <ModalHeader className="modal-title" tag="h4">
            {<GetLanguageString props="finance_unit_manage_account" />}
            <br></br>
            <span className="ur">({unitName})</span>
          </ModalHeader>
          <div className="tabs-container" style={{ margin: "15px" }}>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={activeTab === "1" ? "active" : ""}
                  onClick={() => toggleTab("1")}
                  style={{ cursor: "pointer", fontWeight: "bold" }}
                >
                  <GetLanguageString props="accounts_tab" />
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === "2" ? "active" : ""}
                  onClick={() => toggleTab("2")}
                  style={{ cursor: "pointer", fontWeight: "bold" }}
                >
                  <GetLanguageString props="finance_tab_unit_accounts_sub_category" />
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === "3" ? "active" : ""}
                  onClick={() => toggleTab("3")}
                  style={{ cursor: "pointer", fontWeight: "bold" }}
                >
                  <GetLanguageString props="finance_tab_unit_dimension2_sub_category" />
                </NavLink>
              </NavItem>
            </Nav>
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                {!loading ? (
                  <Form onSubmit={handleSubmit}>
                    <ModalBody style={{ padding: 0 }}>
                      <div
                        className="panel-body"
                        style={{
                          backgroundColor: "#fff",
                          border: "1px solid #e7eaec",
                          borderTop: "none",
                          padding: "25px",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <FormGroup>
                          <Label className="col-form-label" style={{ fontWeight: "bold" }}>
                            {
                              <GetLanguageString props="finance_unit_manage_account_select_dimension" />
                            }
                          </Label>
                          <Select
                            value={selectedUnitDimension}
                            options={dimensionsList}
                            className="basic-single no-drag"
                            classNamePrefix="select"
                            isClearable
                            onChange={(v) => {
                              setSelectedUnitDimension(v ?? null);
                            }}
                          />
                          {errors.dimensionError && (
                            <h4 className="text-error" style={{ color: "#ed5565", marginTop: "5px" }}>{errors.dimensionError}</h4>
                          )}
                        </FormGroup>

                        <FormGroup style={{ marginTop: "15px" }}>
                          <Label className="col-form-label" style={{ fontWeight: "bold" }}>
                            {<GetLanguageString props="finance_unit_manage_account_select_cash_in_hand_account" />}
                          </Label>
                          <Select
                            value={selectedUnitCashInHandAccount}
                            options={accountsList}
                            className="basic-single no-drag"
                            classNamePrefix="select"
                            isClearable
                            onChange={(v) => {
                              setSelectedUnitCashInHandAccount(v ?? null);
                            }}
                          />
                        </FormGroup>

                        <FormGroup style={{ marginTop: "15px" }}>
                          <Label className="col-form-label" style={{ fontWeight: "bold" }}>
                            {<GetLanguageString props="finance_unit_manage_account_select_bank_account" />}
                          </Label>
                          <Select
                            isMulti
                            value={selectedUnitBankAccounts}
                            options={accountsList}
                            className="basic-multi-select no-drag"
                            classNamePrefix="select"
                            isClearable
                            onChange={(v) => {
                              setSelectedUnitBankAccounts(v || []);
                            }}
                          />
                        </FormGroup>
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <Button color="white" onClick={toggle}>
                        {<GetLanguageString props="common_cancel" />}
                      </Button>
                      <Button color="primary" disabled={buttonDisable} type="submit">
                        {<GetLanguageString props="common_save_changes" />}
                      </Button>
                    </ModalFooter>
                  </Form>
                ) : (
                  LoadingSpinner()
                )}
              </TabPane>
              <TabPane tabId="2">
                <style>{`
                  .btn-update-accounts {
                    transition: all 0.2s ease-in-out !important;
                  }
                  .btn-update-accounts:hover {
                    background-color: #15947a !important;
                    border-color: #15947a !important;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 6px rgba(24, 166, 137, 0.15) !important;
                  }
                  .btn-update-dimensions {
                    transition: all 0.2s ease-in-out !important;
                  }
                  .btn-update-dimensions:hover {
                    background-color: #1872ab !important;
                    border-color: #1872ab !important;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 6px rgba(28, 132, 198, 0.15) !important;
                  }
                  .category-row {
                    transition: background-color 0.15s ease-in-out !important;
                  }
                  .category-row:hover {
                    background-color: #f8fafc !important;
                  }
                `}</style>
                <ModalBody style={{ padding: 0 }}>
                  <div
                    className="panel-body"
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #e7eaec",
                      borderTop: "none",
                      padding: "25px",
                      borderRadius: "0 0 4px 4px",
                    }}
                  >
                    {!loading ? (
                      accountSubCategories.length !== 0 ? (
                        <div className="table-responsive" style={{ borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                          <Table hover borderless className="align-middle mb-0" style={{ backgroundColor: "#fff" }}>
                            <thead style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                              <tr>
                                <th style={{ width: "6%", padding: "14px 16px", color: "#475569", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: "700" }}>#</th>
                                <th style={{ width: "24%", padding: "14px 16px", color: "#475569", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: "700" }}>
                                  {<GetLanguageString props="finance_tab_category" />}
                                </th>
                                <th style={{ width: "50%", padding: "14px 16px", color: "#475569", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: "700" }}>{<GetLanguageString props="funds_account" />}</th>
                                <th className="text-end text-right" style={{ width: "20%", padding: "14px 16px", color: "#475569", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: "700" }}>
                                  {<GetLanguageString props="common_action" />}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {accountSubCategories.map((record, index) => {
                                return (
                                  <tr key={index} className="category-row" style={{ borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                                    <td style={{ padding: "14px 16px", color: "#64748b", fontSize: "13px" }}>{index + 1}</td>
                                    <td className={isUrdu(record.category) ? "ur" : ""} style={{ padding: "14px 16px", fontWeight: "600", color: "#1e293b", fontSize: "13px" }}>
                                      {record.category}
                                    </td>
                                    <td style={{ padding: "14px 16px" }}>
                                      {record.accounts !== null &&
                                        record.accounts?.length > 0 ? (
                                        record.accounts?.map((record1, index1) => {
                                          return (
                                            <span
                                              key={index1}
                                              style={{
                                                display: "inline-block",
                                                backgroundColor: "#f0f9ff",
                                                color: "#0284c7",
                                                border: "1px solid #bae6fd",
                                                padding: "4px 10px",
                                                borderRadius: "20px",
                                                fontSize: "11px",
                                                fontWeight: "600",
                                                marginRight: "6px",
                                                marginBottom: "4px"
                                              }}
                                            >
                                              {record1.label}
                                            </span>
                                          );
                                        })
                                      ) : (
                                        <span style={{ color: "#94a3b8", fontSize: "14px" }}>
                                          -
                                        </span>
                                      )}
                                    </td>
                                    <td className="text-right text-end" style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                                      <Button
                                        className="btn-update-accounts"
                                        color="info"
                                        size="sm"
                                        style={{
                                          borderRadius: "20px",
                                          padding: "5px 14px",
                                          fontSize: "11px",
                                          fontWeight: "600",
                                          color: "#ffffff",
                                          border: "1px solid #18A689",
                                          backgroundColor: "#18A689",
                                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                          transition: "all 0.2s"
                                        }}
                                        onClick={() => handleOnUpdateAccounts(record)}
                                      >
                                        {
                                          <GetLanguageString props="finance_tab_finance_accounts_update" />
                                        }
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <NoResult />
                      )
                    ) : (
                      LoadingSpinner()
                    )}
                  </div>
                </ModalBody>
                <ModalFooter>
                  <Button color="white" onClick={toggle}>
                    {<GetLanguageString props="common_cancel" />}
                  </Button>
                </ModalFooter>
              </TabPane>
              <TabPane tabId="3">
                <ModalBody style={{ padding: 0 }}>
                  <div
                    className="panel-body"
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #e7eaec",
                      borderTop: "none",
                      padding: "25px",
                      borderRadius: "0 0 4px 4px",
                    }}
                  >
                    {!loading ? (
                      dimensionSubCategories.length !== 0 ? (
                        <div className="table-responsive" style={{ borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                          <Table hover borderless className="align-middle mb-0" style={{ backgroundColor: "#fff" }}>
                            <thead style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                              <tr>
                                <th style={{ width: "6%", padding: "14px 16px", color: "#475569", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: "700" }}>#</th>
                                <th style={{ width: "24%", padding: "14px 16px", color: "#475569", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: "700" }}>
                                  {<GetLanguageString props="finance_tab_category" />}
                                </th>
                                <th style={{ width: "50%", padding: "14px 16px", color: "#475569", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: "700" }}>{<GetLanguageString props="finance_finance_dimensions" />}</th>
                                <th className="text-end text-right" style={{ width: "20%", padding: "14px 16px", color: "#475569", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: "700" }}>
                                  {<GetLanguageString props="common_action" />}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {dimensionSubCategories.map((record, index) => {
                                return (
                                  <tr key={index} className="category-row" style={{ borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" }}>
                                    <td style={{ padding: "14px 16px", color: "#64748b", fontSize: "13px" }}>{index + 1}</td>
                                    <td className={isUrdu(record.category) ? "ur" : ""} style={{ padding: "14px 16px", fontWeight: "600", color: "#1e293b", fontSize: "13px" }}>
                                      {record.category}
                                    </td>
                                    <td style={{ padding: "14px 16px" }}>
                                      {record.dimensions !== null &&
                                        record.dimensions?.length > 0 ? (
                                        record.dimensions?.map((record1, index1) => {
                                          return (
                                            <span
                                              key={index1}
                                              style={{
                                                display: "inline-block",
                                                backgroundColor: "#f0fdf4",
                                                color: "#18A689",
                                                border: "1px solid #bbf7d0",
                                                padding: "4px 10px",
                                                borderRadius: "20px",
                                                fontSize: "11px",
                                                fontWeight: "600",
                                                marginRight: "6px",
                                                marginBottom: "4px"
                                              }}
                                            >
                                              {record1.label}
                                            </span>
                                          );
                                        })
                                      ) : (
                                        <span style={{ color: "#94a3b8", fontSize: "14px" }}>
                                          -
                                        </span>
                                      )}
                                    </td>
                                    <td className="text-right text-end" style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                                      <Button
                                        className="btn-update-dimensions"
                                        color="primary"
                                        size="sm"
                                        style={{
                                          borderRadius: "20px",
                                          padding: "5px 14px",
                                          fontSize: "11px",
                                          fontWeight: "600",
                                          color: "#ffffff",
                                          border: "1px solid #1c84c6",
                                          backgroundColor: "#1c84c6",
                                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                          transition: "all 0.2s"
                                        }}
                                        onClick={() => handleOnUpdateDimensions(record)}
                                      >
                                        {
                                          <GetLanguageString props="finance_tab_finance_dimensions_update" />
                                        }
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <NoResult />
                      )
                    ) : (
                      LoadingSpinner()
                    )}
                  </div>
                </ModalBody>
                <ModalFooter>
                  <Button color="white" onClick={toggle}>
                    {<GetLanguageString props="common_cancel" />}
                  </Button>
                </ModalFooter>
              </TabPane>
            </TabContent>
          </div>
        </Modal>
      </Draggable>
    </div>
  );
};

export default ManageUnitAccounts;
